import {
  Alert,
  Breadcrumbs,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import {
  Folder as FolderIcon,
  Home as HomeIcon,
} from "@mui/icons-material";
import React, { useEffect, useMemo, useState } from "react";

import { isDirectory } from "./FileGrid";
import { fetchPath } from "./app/transfer";
import {
  MoveSource,
  normalizeDirectory,
  validateMoveDestination,
} from "./app/move";

function MoveDestinationDialog({
  open,
  sources,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  sources: MoveSource[];
  onCancel: () => void;
  onConfirm: (destination: string) => void;
}) {
  const [path, setPath] = useState("");
  const [folders, setFolders] = useState<
    Awaited<ReturnType<typeof fetchPath>>
  >([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    if (!open) return;
    setPath("");
    setError(null);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoading(true);
    setError(null);
    fetchPath(path)
      .then((items) => {
        if (active) setFolders(items.filter(isDirectory));
      })
      .catch((cause) => {
        if (active)
          setError(
            cause instanceof Error ? cause : new Error(String(cause))
          );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [open, path, retry]);

  const parts = useMemo(
    () => path.replace(/\/$/, "").split("/").filter(Boolean),
    [path]
  );
  const invalid = validateMoveDestination(sources, path);

  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth="sm">
      <DialogTitle>Choose destination</DialogTitle>
      <DialogContent>
        <Breadcrumbs separator="›" sx={{ marginBottom: 1 }}>
          <Button
            aria-label="Drive root"
            onClick={() => setPath("")}
            sx={{ minWidth: 0 }}
          >
            <HomeIcon />
          </Button>
          {parts.map((part, index) => (
            <Button
              key={`${part}-${index}`}
              onClick={() =>
                setPath(normalizeDirectory(parts.slice(0, index + 1).join("/")))
              }
            >
              {part}
            </Button>
          ))}
        </Breadcrumbs>

        <Typography variant="body2" color="text.secondary">
          Destination: /{path}
        </Typography>

        {loading ? (
          <CircularProgress aria-label="Loading folders" sx={{ margin: 2 }} />
        ) : error ? (
          <Alert
            severity="error"
            action={
              <Button onClick={() => setRetry((value) => value + 1)}>
                Retry
              </Button>
            }
            sx={{ marginTop: 2 }}
          >
            {error.message}
          </Alert>
        ) : (
          <List>
            {folders.map((folder) => (
              <ListItemButton
                key={folder.key}
                onClick={() => setPath(normalizeDirectory(folder.key))}
              >
                <ListItemIcon>
                  <FolderIcon />
                </ListItemIcon>
                <ListItemText primary={folder.key.split("/").pop()} />
              </ListItemButton>
            ))}
            {!folders.length && (
              <Typography color="text.secondary" sx={{ padding: 2 }}>
                No folders
              </Typography>
            )}
          </List>
        )}

        {invalid.length > 0 && (
          <Alert severity="warning">
            {invalid.join(", ")} cannot be moved to this destination.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          variant="contained"
          disabled={loading || Boolean(error) || invalid.length > 0}
          onClick={() => onConfirm(path)}
        >
          Move here
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default MoveDestinationDialog;
